import { MANAGER_ROLES } from '@performance-crm/shared';
import { Building, FileText, Handshake, TrendingDown, Users } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Select } from '@/components/ui/select';
import { useAuth } from '@/lib/auth-context';
import { useCompanyScope } from '@/lib/company-context';
import { useProjects } from '@/lib/queries';
import { BuyersTab } from './BuyersTab';
import { DealsTab } from './DealsTab';
import { QuotesTab } from './QuotesTab';
import { SeparationsTab } from './SeparationsTab';
import { StockTab } from './StockTab';

type Tab = 'stock' | 'proformas' | 'procesos' | 'caidas' | 'compradores';
const TABS: Array<[Tab, string, typeof Building]> = [
  ['stock', 'Stock', Building],
  ['proformas', 'Proformas', FileText],
  ['procesos', 'Separaciones y ventas', Handshake],
  ['caidas', 'Caídas y cierre', TrendingDown],
  ['compradores', 'Perfil del comprador', Users],
];

/** Módulo comercial: stock por proyecto, proformas, procesos y perfil del comprador. */
export function CommercialPage() {
  const { user } = useAuth();
  const { companies, active } = useCompanyScope();
  const [params, setParams] = useSearchParams();
  const companyId = params.get('companyId') ?? active ?? companies[0]?.companyId;
  const { data: projects } = useProjects();
  const mine = (projects ?? []).filter((p) => p.companyId === companyId);
  const projectId = params.get('projectId') ?? '';
  const tab = (params.get('tab') as Tab) ?? 'stock';
  const isManager = !!user?.memberships.some(
    (m) => m.companyId === companyId && MANAGER_ROLES.includes(m.role),
  );
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k === 'companyId') next.delete('projectId');
    setParams(next, { replace: true });
  };
  const effectiveProject = projectId || (tab === 'stock' ? (mine[0]?.id ?? '') : '');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Comercial</h1>
          <p className="text-muted-foreground text-sm">
            Stock de unidades, proformas, separaciones / ventas, caídas y el perfil de quien compra.
          </p>
        </div>
        <div className="bg-card flex flex-wrap gap-1 rounded-md border p-1">
          {TABS.map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => set('tab', key)}
              className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm ${tab === key ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
              data-testid={`tab-${key}`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-card grid gap-2 rounded-lg border p-3 md:grid-cols-3">
        {companies.length > 1 && (
          <Select
            value={companyId ?? ''}
            onChange={(e) => set('companyId', e.target.value)}
            aria-label="Empresa"
          >
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>
                {c.companyName}
              </option>
            ))}
          </Select>
        )}
        <Select
          value={effectiveProject}
          onChange={(e) => set('projectId', e.target.value)}
          aria-label="Proyecto"
        >
          {tab !== 'stock' && <option value="">Todos los proyectos</option>}
          {mine.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {!p.active ? ' (inactivo)' : ''}
            </option>
          ))}
        </Select>
      </div>
      {companyId && tab === 'stock' && effectiveProject && (
        <StockTab companyId={companyId} projectId={effectiveProject} canManage={isManager} />
      )}
      {companyId && tab === 'proformas' && (
        <QuotesTab companyId={companyId} projectId={projectId || undefined} />
      )}
      {companyId && tab === 'procesos' && (
        <DealsTab companyId={companyId} projectId={projectId || undefined} />
      )}
      {companyId && tab === 'caidas' && (
        <SeparationsTab companyId={companyId} projectId={projectId || undefined} />
      )}
      {companyId && tab === 'compradores' && (
        <BuyersTab companyId={companyId} projectId={projectId || undefined} />
      )}
    </div>
  );
}
